import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { useUser } from '../../utils/auth';
import { EDUCATION_QUALIFICATIONS } from '../lib/constants';
import { countriesList } from '../lib/countriesList';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';

// Select component for dropdowns
const Select = ({ label, value, onChange, options, placeholder, isLoading }) => (
  <div className="relative">
    <label className="block text-gray-700 mb-2">{label}</label>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
      // remove the default design
      style={{ appearance: 'none' }}
    >
      <option value="" disabled>{isLoading ? "Loading..." : placeholder}</option>
      {options.map((option) => (
        <option key={option} value={option}>
          {option}
        </option>
      ))}
    </select>
    <div className="absolute inset-y-0 right-0 top-8 flex items-center pr-2 pointer-events-none">
      <svg className="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
        <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
      </svg>
    </div>
  </div>
);

// SearchableSelect component for searchable dropdowns
const SearchableSelect = ({ label, value, onChange, options, placeholder, isLoading, onOpen }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [displayValue, setDisplayValue] = useState('');
  const wrapperRef = useRef(null);

  useEffect(() => {
    setDisplayValue(value || '');
  }, [value]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredOptions = options.filter(option =>
    option.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleInputClick = () => {
    setIsOpen(true);
    if (onOpen) onOpen();
  };

  const handleOptionSelect = (option) => {
    onChange(option);
    setDisplayValue(option);
    setSearchTerm('');
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={wrapperRef}>
      <label className="block text-gray-700 mb-2">{label}</label>
      <div className="relative">
        <input
          type="text"
          value={isOpen ? searchTerm : displayValue}
          onChange={(e) => setSearchTerm(e.target.value)}
          onClick={handleInputClick}
          placeholder={isLoading ? "Loading..." : placeholder}
          className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
          <svg className="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </div>
      </div>
      {isOpen && (
        <div className="absolute z-10 w-full mt-1 bg-white rounded-md shadow-lg border border-gray-300 max-h-60 overflow-auto">
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option) => (
              <div
                key={option}
                className="px-3 py-2 hover:bg-gray-100 cursor-pointer"
                onClick={() => handleOptionSelect(option)}
              >
                {option}
              </div>
            ))
          ) : (
            <div className="px-3 py-2 text-gray-500">No options found</div>
          )}
        </div>
      )}
    </div>
  );
};

const ProfileSection = () => {
  const { user, isLoading } = useUser();
  const { updateUser } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phoneNumber: '',
    currentEducation: '',
    educationCountry: '',
    oldPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [isPhoneVerified, setIsPhoneVerified] = useState(false);
  
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  // States for dropdown options
  const [countries, setCountries] = useState([]);
  const [isLoadingCountries, setIsLoadingCountries] = useState(false);

  // Update form data when user data loads
  useEffect(() => {
    if (user) {
      setFormData(prevData => ({
        ...prevData,
        name: user.name || '',
        email: user.email || '',
        phoneNumber: user.phoneNumber || '',
        currentEducation: user.currentEducation || '',
        educationCountry: user.educationCountry || '',
      }));
    }
  }, [user]);

  // Load countries when component mounts
  const fetchCountries = () => {
    if (countries.length === 0 && !isLoadingCountries) {
      setIsLoadingCountries(true);
      const countryNames = countriesList.sort();
      setCountries(countryNames);
      setIsLoadingCountries(false);
    }
  };

  // Fetch countries when component mounts
  useEffect(() => {
    fetchCountries();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle dropdown change
  const handleDropdownChange = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Check if phone number was changed and is not verified
      if (formData.phoneNumber !== user?.phoneNumber) {
        setError('Please verify your phone number before saving changes');
        setLoading(false);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
      
      // Password validation for update
      if (formData.newPassword) {
        if (formData.newPassword !== formData.confirmPassword) {
          setError('New passwords do not match');
          setLoading(false);
          return;
        }
        
        if (!formData.oldPassword) {
          setError('Current password is required to set a new password');
          setLoading(false);
          return;
        }
      }

      // Make API call to update user profile
      const token = localStorage.getItem('token');
      const userId = user?.id || localStorage.getItem('userId');
      
      const response = await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/users/update-profile`,
        {
          id: userId,
          name: formData.name,
          email: formData.email,
          phoneNumber: formData.phoneNumber,
          currentEducation: formData.currentEducation,
          educationCountry: formData.educationCountry,
          ...(formData.newPassword && {
            oldPassword: formData.oldPassword,
            newPassword: formData.newPassword,
          }),
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success) {
        // Update local user state
        updateUser(response.data.user);
        setSuccess('Profile updated successfully');
        setIsEditing(false);
        
        // Clear sensitive data
        setFormData((prev) => ({
          ...prev,
          oldPassword: '',
          newPassword: '',
          confirmPassword: '',
        }));
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      setError(error.response?.data?.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleSendOTP = async () => {
    if (!formData.phoneNumber) {
      setError('Please enter a phone number');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/auth/send-profile-otp`,
        { phoneNumber: formData.phoneNumber },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      
      if (response.data.success) {
        setOtpSent(true);
        setSuccess('OTP sent successfully');
      }
    } catch (error) {
      console.error('Error sending OTP:', error);
      setError(error.response?.data?.message || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (!otp) {
      setError('Please enter the OTP');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/auth/verify-profile-otp`,
        { 
          phoneNumber: formData.phoneNumber,
          otp 
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      
      if (response.data.success) {
        // Update user with verified phone
        updateUser({
          ...user,
          phoneNumber: formData.phoneNumber,
          isPhoneVerified: true,
        });
        
        // Set isPhoneVerified to true so UI updates immediately
        setIsPhoneVerified(true);
        
        setSuccess('Phone number verified successfully');
        setIsVerifying(false);
        setOtpSent(false);
        setOtp('');
      }
    } catch (error) {
      console.error('Error verifying OTP:', error);
      setError(error.response?.data?.message || 'Failed to verify OTP');
    } finally {
      setLoading(false);
    }
  };

  // Toggle password visibility functions
  const toggleOldPasswordVisibility = () => {
    setShowOldPassword(!showOldPassword);
  };

  const toggleNewPasswordVisibility = () => {
    setShowNewPassword(!showNewPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const handleEditProfile = () => {
    if(formData.phoneNumber &&
      formData.phoneNumber === user?.phoneNumber){
        setIsPhoneVerified(true);
    }  
    setIsEditing(true);
  };

  useEffect(() => {
    // if phone number changes in form then set isPhoneVerified to false
    if(formData.phoneNumber === user?.phoneNumber){
      setIsPhoneVerified(true);
    }
    else{
      setIsPhoneVerified(false);
    }
  }, [formData.phoneNumber]);

  // Show loading state while user data is being fetched
  if (isLoading) {
    return (
      <div className="max-w-2xl mx-auto p-6 flex justify-center">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-48 mb-4"></div>
          <div className="h-40 bg-gray-200 rounded w-full"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">My Profile</h1>
        {!isEditing && (
          <button
            onClick={() => handleEditProfile()}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Edit Profile
          </button>
        )}
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
          {error}
        </div>
      )}

      {success && (
        <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded">
          {success}
        </div>
      )}

      {isVerifying ? (
        <div className="bg-blue-50 rounded-lg p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4">Verify Phone Number</h2>
          <div className="mb-4">
            <label className="block text-gray-700 mb-2">Phone Number</label>
            <div className="phone-input-container">
              <PhoneInput
                country={'in'}
                value={formData.phoneNumber}
                onChange={(value) => setFormData({ ...formData, phoneNumber: value })}
                inputProps={{
                  className: 'w-full py-3 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors pl-12',
                  disabled: otpSent
                }}
                containerClass="w-full phone-input-container"
                buttonStyle={{ background: 'transparent', border: 'none' }}
                dropdownStyle={{ width: '300px' }}
                containerStyle={{ 
                  width: '100%'
                }}
                inputStyle={{
                  width: '100%',
                  paddingLeft: '48px'
                }}
                disabled={otpSent}
              />
            </div>
          </div>
          
          {!otpSent ? (
            <button
              onClick={handleSendOTP}
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors w-full mb-4"
            >
              {loading ? 'Sending...' : 'Send OTP'}
            </button>
          ) : (
            <>
              <div className="mb-4">
                <label className="block text-gray-700 mb-2">Enter OTP</label>
                <input
                  type="text"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter OTP sent to your phone"
                />
              </div>
              <div className="flex space-x-4">
                <button
                  onClick={handleVerifyOTP}
                  disabled={loading}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  {loading ? 'Verifying...' : 'Verify OTP'}
                </button>
                <button
                  onClick={handleSendOTP}
                  disabled={loading}
                  className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                >
                  Resend OTP
                </button>
              </div>
            </>
          )}
          
          <button
            onClick={() => {
              setIsVerifying(false);
              setOtpSent(false);
              setOtp('');
              setError('');
              setSuccess('');
            }}
            className="mt-4 text-blue-600 hover:underline"
          >
            Cancel
          </button>
        </div>
      ) : isEditing ? (
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-gray-700 mb-2">Full Name</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Your full name"
              />
            </div>
            
            <div>
              <label className="block text-gray-700 mb-2">Email Address</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Your email address"
              />
            </div>
            
            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="block text-gray-700">Phone Number</label>
                {isPhoneVerified ? (
                    <span className="text-green-500 text-sm flex items-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Verified
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => {
                        setIsVerifying(true);
                        setError('');
                        setSuccess('');
                      }}
                      className="text-sm text-blue-600 hover:underline"
                    >
                      Verify Phone
                    </button>
                  )
                }
              </div>
              <div className="phone-input-container">
                <PhoneInput
                  country={'in'}
                  value={formData.phoneNumber}
                  onChange={(value) => setFormData({ ...formData, phoneNumber: value })}
                  inputProps={{
                    className: 'w-full py-3 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors pl-12',
                  }}
                  containerClass="w-full phone-input-container"
                  buttonStyle={{ background: 'transparent', border: 'none' }}
                  dropdownStyle={{ width: '300px' }}
                  containerStyle={{ 
                    width: '100%'
                  }}
                  inputStyle={{
                    width: '100%',
                    paddingLeft: '48px'
                  }}
                />
              </div>
              <style jsx="true">{`
                .phone-input-container .react-tel-input .selected-flag {
                  padding: 0 6px 0 8px;
                  background: transparent !important;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                }
                .phone-input-container .react-tel-input .selected-flag:hover, 
                .phone-input-container .react-tel-input .selected-flag:focus,
                .phone-input-container .react-tel-input .selected-flag.open {
                  background: transparent !important;
                }
                .phone-input-container .react-tel-input .selected-flag .flag {
                  position: static;
                  transform: none;
                  margin: auto;
                }
                .phone-input-container .react-tel-input .form-control {
                  padding-left: 48px;
                }
              `}</style>
            </div>
            
            <div>
              <Select
                label="Current Education"
                value={formData.currentEducation}
                onChange={(value) => handleDropdownChange("currentEducation", value)}
                options={EDUCATION_QUALIFICATIONS}
                placeholder="Select your qualification"
              />
            </div>
            
            <div>
              <SearchableSelect
                label="Education Country"
                value={formData.educationCountry}
                onChange={(value) => handleDropdownChange("educationCountry", value)}
                options={countries}
                placeholder="Select country"
                isLoading={isLoadingCountries}
                onOpen={fetchCountries}
              />
            </div>
          </div>
          
          <div className="border-t border-gray-200 pt-6 mt-6">
            <h3 className="text-lg font-semibold mb-4">Update Password</h3>
            <div className="grid grid-cols-1 gap-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-gray-700 mb-2">Current Password</label>
                  <div className="relative">
                    <input
                      type={showOldPassword ? "text" : "password"}
                      name="oldPassword"
                      value={formData.oldPassword}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Your current password"
                    />
                    <button
                      type="button"
                      onClick={toggleOldPasswordVisibility}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
                    >
                      {showOldPassword ? (
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                        </svg>
                      ) : (
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                          <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-gray-700 mb-2">New Password</label>
                  <div className="relative">
                    <input
                      type={showNewPassword ? "text" : "password"}
                      name="newPassword"
                      value={formData.newPassword}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Your new password"
                    />
                    <button
                      type="button"
                      onClick={toggleNewPasswordVisibility}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
                    >
                      {showNewPassword ? (
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                        </svg>
                      ) : (
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                          <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>
                
                <div>
                  <label className="block text-gray-700 mb-2">Confirm Password</label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Confirm new password"
                    />
                    <button
                      type="button"
                      onClick={toggleConfirmPasswordVisibility}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
                    >
                      {showConfirmPassword ? (
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                        </svg>
                      ) : (
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                          <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="flex justify-end space-x-4 mt-6">
            <button
              type="button"
              onClick={() => {
                setIsEditing(false);
                // Reset form data to current user data
                if (user) {
                  setFormData({
                    name: user.name || '',
                    email: user.email || '',
                    phoneNumber: user.phoneNumber || '',
                    currentEducation: user.currentEducation || '',
                    educationCountry: user.educationCountry || '',
                    oldPassword: '',
                    newPassword: '',
                    confirmPassword: '',
                  });
                }
                setError('');
                setSuccess('');
              }}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              {loading ? 'Updating...' : 'Save Changes'}
            </button>
          </div>
        </form>
      ) : (
        <div className="bg-white rounded-lg">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="border-b pb-4">
              <p className="text-sm text-gray-500 mb-1">Full Name</p>
              <p className="font-medium">{user?.name || 'Not provided'}</p>
            </div>
            
            <div className="border-b pb-4">
              <p className="text-sm text-gray-500 mb-1">Email Address</p>
              <p className="font-medium">{user?.email || 'Not provided'}</p>
            </div>
            
            <div className="border-b pb-4">
              <p className="text-sm text-gray-500 mb-1">Phone Number</p>
              <div className="flex items-center">
                <p className="font-medium">{user?.phoneNumber || 'Not provided'}</p>
              </div>
            </div>
            
            <div className="border-b pb-4">
              <p className="text-sm text-gray-500 mb-1">Current Education</p>
              <p className="font-medium">{user?.currentEducation || 'Not provided'}</p>
            </div>
            
            <div className="border-b pb-4">
              <p className="text-sm text-gray-500 mb-1">Education Country</p>
              <p className="font-medium">{user?.educationCountry || 'Not provided'}</p>
            </div>
          </div>
          
          <div className="mt-6 border-t border-gray-200 pt-6">
            <h3 className="text-lg font-semibold mb-2">Password</h3>
            <p className="text-gray-500">Your password is securely stored. You can update it by clicking the Edit Profile button.</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileSection;